import React, { useEffect, useMemo, useState } from 'react'
import Modal from 'components/atoms/Modal'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getSharedUnitsList, selectBpUnits } from 'ducks/units/selectors'
import { fetchBpUnitsRoutine } from 'ducks/units/actions'
import * as unitsService from 'services/unitsService'
import { isEmpty } from 'ramda'
import ShareUnitSelect from 'components/ShareUnitSelect'
import { getSharedUnitsListForFileRoutine, shareFileRoutine } from 'ducks/files/actions'
import { SkeletonRows } from 'components/Skeleton'

const shareUnitsPoolCache = new Map()

const ShareFileModal = ({
  open,
  onClose,
  file,
  bp: bpProp = null
}) => {
  const [values, setValues] = useState([])
  const dispatch = useDispatch()
  const selectedBp = useSelector(getSelectedBp)
  const storeBpUnits = useSelector(selectBpUnits)
  const sharedUnitsList = useSelector(getSharedUnitsList)
  const [fetchedUnits, setFetchedUnits] = useState(null)
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)
  const needsUnitsFetch = Boolean(bpProp?.id) && bpProp.id !== selectedBp?.id
  const bp = bpProp ?? selectedBp
  const bpUnits = needsUnitsFetch ? (fetchedUnits || []) : storeBpUnits

  useEffect(() => {
    if (!open || !needsUnitsFetch) return

    const cached = shareUnitsPoolCache.get(bpProp.id)
    if (cached) {
      setFetchedUnits(cached)
      return
    }

    let cancelled = false
    setIsLoadingUnits(true)
    unitsService.fetchBpUnits({ bpId: bpProp.id })
      .then(response => {
        const list = response?.data?.data || []
        shareUnitsPoolCache.set(bpProp.id, list)
        if (!cancelled) setFetchedUnits(list)
      })
      .catch(error => {
        console.error('Failed to fetch building units:', error)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUnits(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, needsUnitsFetch, bpProp?.id])

  const handleSubmit = () => {
    const unitIds = values.map(unit => unit.value)
    dispatch(shareFileRoutine({
      fileId: file.id,
      buildingUnitPassportsIds: unitIds,
      bpId: bp.id
    }))
    onClose()
    dispatch(fetchBpUnitsRoutine({ bpId: bp.id }))
  }

  useEffect(() => {
    if (sharedUnitsList) {
      const selectedUnits = sharedUnitsList.map(unit => ({
        label: unit.name,
        value: unit.id,
        uprn: unit.uprn,
        floorNumbers: unit.floorNumbers
      }))
      setValues(selectedUnits)
    }
  }, [sharedUnitsList])

  const unitsOptions = useMemo(() => {
    if (!isEmpty(bpUnits)) {
      return bpUnits.map(unit => ({
        label: unit.name,
        value: unit.id,
        uprn: unit.uprn,
        floorNumbers: unit.floorNumbers
      }))
    } else {
      return []
    }
  }, [bpUnits])

  useEffect(() => {
    open && dispatch(getSharedUnitsListForFileRoutine(file.id))
  }, [open])
  return (
    <Modal
      title='File sharing'
      open={open}
      onClose={onClose}
    >
      <FormWrapper>
        {isLoadingUnits ? (
          <SkeletonRows rows={4} gap={16} widths={['60%', '30%']} />
        ) : (
          <ShareUnitSelect
            name='units'
            placeholder='Units'
            options={unitsOptions}
            multiple
            values={values}
            label='Units'
            onChange={setValues}
            noMargin
            onSubmit={handleSubmit}
          />
        )}
      </FormWrapper>
    </Modal>
  )
}

export default ShareFileModal

const FormWrapper = styled.div`
  width: 500px;
  margin: 0 auto 30px;

  @media (max-width: 768px) {
    width: 100%;
    margin: 0;
    box-sizing: border-box;
  }
`
